/**
 * ShareButton — social sharing for listing detail
 *
 * Uses Web Share API when available, with fallback copy-to-clipboard.
 */

"use client";

import { useState, useCallback } from "react";
import { Share2, Copy, Check, Facebook, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ShareButtonProps {
    title: string;
    text: string;
    url: string;
}

export function ShareButton({ title, text, url }: ShareButtonProps) {
    const [copied, setCopied] = useState(false);
    const fullUrl = typeof window !== "undefined" ? `${window.location.origin}${url}` : url;

    const handleNativeShare = useCallback(async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title, text, url: fullUrl });
            } catch (err) {
                if ((err as Error).name !== "AbortError") {
                    console.error("Share failed:", err);
                }
            }
        }
    }, [title, text, fullUrl]);

    const handleCopyLink = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(fullUrl);
            setCopied(true);
            toast.success("Lien copié !");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Impossible de copier le lien");
        }
    }, [fullUrl]);

    const shareOnFacebook = useCallback(() => {
        window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`,
            "_blank",
            "noopener,noreferrer,width=600,height=400",
        );
    }, [fullUrl]);

    const shareOnTwitter = useCallback(() => {
        window.open(
            `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(fullUrl)}`,
            "_blank",
            "noopener,noreferrer,width=600,height=400",
        );
    }, [text, fullUrl]);

    // If native share is available, use it directly
    if (typeof navigator !== "undefined" && "share" in navigator) {
        return (
            <Button
                variant="outline"
                size="sm"
                onClick={handleNativeShare}
                className="border-hp-gray/20 text-hp-gray-light hover:text-hp-purple hover:border-hp-purple rounded-xl"
            >
                <Share2 className="mr-2 h-4 w-4" aria-hidden="true" />
                Partager
            </Button>
        );
    }

    // Fallback: dropdown with share options
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="border-hp-gray/20 text-hp-gray-light hover:text-hp-purple hover:border-hp-purple rounded-xl"
                >
                    <Share2 className="mr-2 h-4 w-4" aria-hidden="true" />
                    Partager
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
                    {copied ? (
                        <Check className="mr-2 h-4 w-4 text-hp-teal" />
                    ) : (
                        <Copy className="mr-2 h-4 w-4" />
                    )}
                    Copier le lien
                </DropdownMenuItem>
                <DropdownMenuItem onClick={shareOnFacebook} className="cursor-pointer">
                    <Facebook className="mr-2 h-4 w-4" />
                    Facebook
                </DropdownMenuItem>
                <DropdownMenuItem onClick={shareOnTwitter} className="cursor-pointer">
                    <Twitter className="mr-2 h-4 w-4" />
                    Twitter / X
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
